import { Card, CardContent, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashType } from '../../constants/trash';
import { formatAddress } from '../../helpers/geo';
import { getAggregatedErrors, getErrors } from '../../helpers/trash';
import { Namespace } from '../../locales/translations';
import { useAppSelector, useSortingRules } from '../../store/hooks';
import { selectAllBatches } from '../../store/reducers/batches/batch_reducer';
import { selectAllRfidBatches } from '../../store/reducers/batches/rfid_batch_reducer';
import ActionButton from '../_include/Buttons/ActionButton';
import { useParams } from 'react-router-dom';
import { selectAllCampaignAddresses } from '../../store/reducers/awareness_campaigns/addresses';
import moment from 'moment';
import { AwarenessCampaignStatus } from '../../models/AwarenessCampaign';

interface KeyNumbersCardProps {
    onSeeMoreClick: () => void;
    openReport?: () => void;
}

const KeyNumbersCard: React.FC<KeyNumbersCardProps> = ({ onSeeMoreClick, openReport }) => {
    const { errorsClasses } = useSortingRules();
    const showRfid = useAppSelector(state => state.batch.rfid.showRfidsBatches);

    const batches = useAppSelector(state => showRfid ? selectAllRfidBatches(state) : selectAllBatches(state));
    const rfidValue = useAppSelector(state => state.batch.rfid.rfidValue);
    const { t } = useTranslation([Namespace.STATS, Namespace.COMMONS, Namespace.RFID, Namespace.ADDRESSES]);

    const { addressKey } = useParams<{ addressKey: string }>();
    const addressData = useAppSelector(state => selectAllCampaignAddresses(state).find((address) => address.addressKey === addressKey!));
    const isExternalSource = addressData?.fromExternalSource;

    const campaignCompleted = useAppSelector(state => state.awareness_campaign.selected.selectedAwarenessCampaign?.status === AwarenessCampaignStatus.COMPLETED);

    // Only calculate stats if not from external source
    const aggregatedErrors = useMemo(() =>
        !isExternalSource ? getAggregatedErrors(errorsClasses, batches) : {},
        [errorsClasses, batches, isExternalSource]);

    const mostCommonMistake = !isExternalSource
        ? Object.keys(aggregatedErrors).reduce((a, b) =>
            aggregatedErrors[a as TrashType]! > aggregatedErrors[b as TrashType]! ? a : b, "") as TrashType
        : "";

    const totalBatches = batches.length;

    const batchesWithErrors = !isExternalSource
        ? batches.filter(batch => {
            const errors = getErrors(errorsClasses, batch.results);
            const errorsCount = Object.values(errors).reduce((acc, count) => acc + count, 0);
            return errorsCount > 0;
        }).length
        : 0;

    const averageQuality = !isExternalSource && totalBatches > 0
        ? ((1 - (batchesWithErrors / totalBatches)) * 100).toFixed(0)
        : "100";

    const address = showRfid
        ? `${t("bin_identifier", { ns: Namespace.RFID })}: ${rfidValue}`
        : (addressData?.fromExternalSource || batches.length > 0
            ? formatAddress(addressData?.address!)
            : t("no_address_available", { ns: Namespace.STATS }));

    const latestRefusal = useMemo(() => {
        if (addressData?.refusalReason) {
            const refusalDates = Object.keys(addressData.refusalReason);
            const sortedDates = [...refusalDates].sort((a, b) => moment(b).diff(moment(a)));
            return sortedDates[0] || null;
        }
        return null;
    }, [addressData]);

    // Get refusal count if from external source
    const refusalCount = isExternalSource && addressData?.refusalReason
        ? Object.keys(addressData.refusalReason).length
        : 0;

    return (
        <Card elevation={0}>
            <CardContent style={{ padding: 0 }}>
                <Typography color='#999' fontSize='16px' fontStyle='italic' fontWeight='700' lineHeight='150%'>{address}</Typography>
                <Typography variant="h4">{t("key_numbers", { ns: Namespace.STATS })}</Typography>
                {isExternalSource ? (
                    // External source content
                    <>
                        <Typography variant="body1" paddingY={0.5}>
                            {t("refusal_count", { ns: Namespace.ADDRESSES })}:
                            <Typography component="span" variant="body1" fontWeight="bold" sx={{ ml: 1 }}>
                                {refusalCount}
                            </Typography>
                        </Typography>
                        {addressData?.refusalReason && (
                            <Typography variant="body1" paddingY={0.5}>
                                {t("latest_refusal", { ns: Namespace.ADDRESSES })}:
                                <Typography component="span" variant="body1" fontWeight="bold" sx={{ ml: 1 }}>
                                    {latestRefusal}
                                </Typography>
                            </Typography>
                        )}
                    </>
                ) : (
                    // Regular content for non-external sources
                    <>
                        <Typography variant="body1" paddingBottom={1}>
                            {t("most_common_mistake", { ns: Namespace.STATS })}
                            <Typography component="span" variant="body1" fontWeight="bold">
                                {t(`${mostCommonMistake}`, { ns: Namespace.WASTES })}
                            </Typography>
                        </Typography>
                        <Typography variant="body1" paddingY={0.5}>
                            {t("average_quality", { ns: Namespace.STATS })}
                            <Typography component="span" variant="body1" fontWeight="bold">
                                {averageQuality}%
                            </Typography>
                        </Typography>
                    </>
                )}
                {!isExternalSource && (
                    <Typography paddingTop={1} variant="h5" color="secondary" style={{ cursor: 'pointer' }} onClick={onSeeMoreClick}>
                        {t("see_more", { ns: Namespace.COMMONS })}
                    </Typography>
                )}

                {openReport && addressData && (
                    <ActionButton
                        onClick={openReport}
                        color="success"
                        loading={false}
                        disabled={campaignCompleted}
                        fullWidth
                        style={{ marginTop: 10, marginBottom: 10 }}
                    >
                        {t(addressData.visited ? 'sensitized' : 'mark_as_sensitized', { ns: Namespace.ACTIONS })}
                    </ActionButton>
                )}
            </CardContent>
        </Card>
    );
};

export default KeyNumbersCard;